import type { AppDispatch } from "../../Store";
import * as React from "react";
import { connect } from "react-redux";
import {
  IconButton,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PauseIcon from "@mui/icons-material/Pause";
import { TICKS_PER_HOUR, TICK_MS } from "../../Constants";
import { formatHour, getTimeFromTimeline } from "../../helpers/DateTime";
import { formatMoneyStable, formatWatts } from "../../helpers/Format";
import { navigate } from "../../reducers/Card";
import { isBigScreen, openWindow } from "../../Globals";
import { getNextTutorial, getScenario } from "../../data/Scenarios";
import { quit, setSpeed, startTutorial } from "../../reducers/Game";
import {
  AppStateType,
  FacilityOperatingType,
  GameType,
  SpeedType,
  TickPresentFutureType,
} from "../../Types";
import ScenarioDetailsDialog from "./ScenarioDetailsDialog";
import ConceptIcon from "./ConceptIcon";

/**
 * The game's global state: cash, the date, how fast time is running, how far through the year it
 * is, and whether the lights are currently out.
 *
 * This used to live inside whichever pane happened to be showing, which on desktop meant all of
 * it was reported from the top-left corner of column one. None of it belongs to a single pane,
 * so it spans the app instead -- one bar above the panes on desktop, and the card's own header
 * on a phone, where there is only ever one pane anyway.
 */

export interface StateProps {
  game: GameType;
}

export interface DispatchProps {
  onManual: () => void;
  onSettings: () => void;
  onSpeedChange: (speed: SpeedType) => void;
  onNextTutorial: (scenarioId: number) => void;
  onQuit: () => void;
}

export interface Props extends StateProps, DispatchProps {}

interface SpeedOptionsProps {
  speed: SpeedType;
  onSpeedChange: (speed: SpeedType) => void;
}

/**
 * The speeds, in the order the clock runs them, labelled with how many times faster than SLOW
 * each one is. Derived from the tick rate rather than written down beside it, so the labels
 * cannot drift from what the clock actually does -- and a player picking a speed can see what
 * they are picking rather than inferring it from the number of chevrons on an icon.
 */
const RUNNING_SPEEDS: SpeedType[] = ["SLOW", "NORMAL", "FAST"];

function speedMultiplier(speed: SpeedType): string {
  return Math.round(TICK_MS.SLOW / TICK_MS[speed]) + "×";
}

const WEATHER_DRIVEN_FUELS = new Set([
  "Sun",
  "Wind",
  "Offshore Wind",
  "Airborne Wind",
]);

const LOW_RESERVE_RATIO = 0.1;

type GridHealthState = "stable" | "low-reserve" | "at-limit" | "blackout";

interface GridHealth {
  state: GridHealthState;
  label: string;
  metric: string;
  announcement: string;
}

/** Capacity that could serve demand now, rather than the deliberately dispatched output. */
export function reserveCapacityW(
  game: GameType,
  now: TickPresentFutureType,
): number {
  const available = game.facilities.reduce(
    (total: number, facility: FacilityOperatingType) => {
      if (facility.paused || facility.yearsToBuildLeft > 0) {
        return total;
      }
      if (facility.peakWh) {
        return (
          total +
          Math.min(
            facility.peakW,
            Math.max(0, facility.currentWh) * TICKS_PER_HOUR,
          )
        );
      }
      return (
        total +
        (WEATHER_DRIVEN_FUELS.has(facility.fuel)
          ? Math.max(0, facility.currentW)
          : facility.peakW)
      );
    },
    0,
  );
  return available - now.demandW;
}

/** Turns the live supply margin into the few states a player can act on at a glance. */
export function getGridHealth(
  game: GameType,
  now: TickPresentFutureType,
): GridHealth {
  if (now.supplyW < now.demandW) {
    return {
      state: "blackout",
      label: "Blackout",
      metric: `${formatWatts(now.demandW - now.supplyW)} short`,
      announcement: "Blackout. Demand is higher than supply.",
    };
  }

  const reserveW = Math.max(0, reserveCapacityW(game, now));
  if (reserveW === 0) {
    return {
      state: "at-limit",
      label: "At limit",
      metric: "0W reserve",
      announcement: "Grid at limit. No reserve remains.",
    };
  }

  const reserveRatio = now.demandW > 0 ? reserveW / now.demandW : Infinity;
  if (reserveRatio <= LOW_RESERVE_RATIO) {
    const reservePercent = Math.round(reserveRatio * 100);
    return {
      state: "low-reserve",
      label: "Low reserve",
      metric: `+${formatWatts(reserveW)} reserve (${reservePercent}%)`,
      announcement: "Low reserve.",
    };
  }

  return {
    state: "stable",
    label: "Stable",
    metric: `+${formatWatts(reserveW)} reserve`,
    announcement: "Grid stable.",
  };
}

const SPEED_ARIA_LABELS: { [k in SpeedType]: string } = {
  PAUSED: "pause",
  SLOW: "slow speed",
  NORMAL: "normal speed",
  FAST: "fast speed",
};

// Pulled out of the component so it can be memoised on the handful of things it actually
// depends on, rather than rebuilt on every tick along with the cash readout beside it
function buildSpeedOptions({
  speed,
  onSpeedChange,
}: SpeedOptionsProps): React.JSX.Element {
  // Keep every speed one tap away at every viewport width. The selected treatment says where
  // the clock is now without turning the current speed into a misleading disabled control.
  return (
    <ToggleButtonGroup
      className="speedToggles"
      exclusive
      size="small"
      value={speed}
      onChange={(_e: React.MouseEvent<HTMLElement>, next: SpeedType | null) => {
        // Null is the group reporting that the button already selected was clicked again.
        // The clock is always running at some speed, so there is nothing to deselect to.
        if (next) {
          onSpeedChange(next);
        }
      }}
      aria-label="game speed"
    >
      <ToggleButton value="PAUSED" aria-label={SPEED_ARIA_LABELS.PAUSED}>
        <PauseIcon fontSize="small" />
      </ToggleButton>
      {RUNNING_SPEEDS.map((s: SpeedType) => (
        <ToggleButton key={s} value={s} aria-label={SPEED_ARIA_LABELS[s]}>
          {speedMultiplier(s)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export function GameAppBar(props: Props) {
  const { game, onManual, onNextTutorial, onQuit, onSettings, onSpeedChange } =
    props;
  const date = game.date;
  const now = getTimeFromTimeline(date.minute, game.timeline);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | null>(
    null,
  );
  const [scenarioDetailsOpen, setScenarioDetailsOpen] = React.useState(false);

  const bigScreen = isBigScreen();
  const speed = game.speed;
  // Watching somebody else's run rather than playing your own. The speed controls stay live --
  // being able to pause and fast forward is most of the point of a replay
  const isReplay = !!game.replayPlayback;
  // Undefined outside a tutorial, and on the last one - so this doubles as "is there a next
  // tutorial to offer?" for the menu item below. Also undefined throughout a replay, since a
  // tutorial never sets a score and so never has one to watch
  const nextTutorial = getNextTutorial(game.scenarioId);
  // A tutorial's progress isn't worth resuming, so its menu item stays "Quit" - only a real run
  // gets the "Save & Quit" reminder that leaving keeps it around to come back to
  const isTutorial = !!getScenario(game.scenarioId, game.customScenario)
    ?.tutorialSteps;
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) =>
    setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);
  const handleQuit = React.useCallback(() => {
    onQuit();
    window.setTimeout(() => {
      document.querySelector<HTMLElement>("[data-main-action]")?.focus();
    }, 350);
  }, [onQuit]);

  /**
   * The bar has to re-render every tick to keep the cash and the clock honest, which at FAST is
   * a hundred times a second. Everything else in it -- the speed toggles, menu button and
   * kept-mounted menu -- only changes when the player clicks something, so those subtrees are
   * built once per actual change and handed back as the same elements. React then skips them
   * entirely on the frames in between, which is where a good quarter of the frame budget went.
   */
  const speedOptions = React.useMemo(
    () =>
      buildSpeedOptions({
        speed,
        onSpeedChange,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [speed, onSpeedChange],
  );

  const menu = React.useMemo(
    () => (
      <>
        <IconButton
          data-settings-trigger
          className="gameMenuButton"
          onClick={handleMenuClick}
          aria-label="menu"
          edge="start"
          color="primary"
          size="large"
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          id="gameCardMenu"
          anchorEl={menuAnchorEl}
          keepMounted
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={onManual}>Manual</MenuItem>
          <MenuItem onClick={onSettings}>Options</MenuItem>
          <MenuItem
            onClick={() => {
              setScenarioDetailsOpen(true);
              handleMenuClose();
            }}
          >
            Scenario details
          </MenuItem>
          <MenuItem onClick={() => openWindow("/about.html#feedback")}>
            Send feedback
          </MenuItem>
          {/* Mid-tutorial, the thing a player who has seen enough wants is the next tutorial,
              not the scenario list they would have to go back through to reach it */}
          {nextTutorial && (
            <MenuItem onClick={() => onNextTutorial(nextTutorial.id)}>
              Next tutorial
            </MenuItem>
          )}
          <MenuItem onClick={handleQuit}>
            {isReplay ? "Exit replay" : isTutorial ? "Quit" : "Save & Quit"}
          </MenuItem>
        </Menu>
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      menuAnchorEl,
      onManual,
      onSettings,
      onNextTutorial,
      handleQuit,
      nextTutorial,
      isReplay,
      isTutorial,
      setScenarioDetailsOpen,
    ],
  );

  if (!game.inGame || !now) {
    return <span />;
  }

  const gridHealth = getGridHealth(game, now);
  const inBlackout = gridHealth.state === "blackout";

  return (
    <div id="appbar">
      <div id="topbar">
        <Toolbar className={inBlackout ? "blackout-pulsing" : ""}>
          {menu}
          <Typography variant="h6" className="gameStatus">
            <span className="gameStatusValue">
              {formatMoneyStable(now.cash)}
            </span>
            <span className="weak gameStatusValue">
              {date.month} {date.year}
              {bigScreen ? `, ${formatHour(date)}` : ""}
            </span>
            {inBlackout && (
              <span className="gameStatusBlackout">
                <ConceptIcon concept="blackout" fontSize="small" />
              </span>
            )}
            {isReplay && <span className="replayBadge">REPLAY</span>}
          </Typography>
          <div id="speedChangeButtons">{speedOptions}</div>
        </Toolbar>
      </div>
      <div
        className={`gridHealth gridHealth-${gridHealth.state}`}
        aria-label={`Current grid status: ${gridHealth.label}, ${gridHealth.metric}`}
      >
        <div className="gridHealthSummary">
          <span className="gridHealthState">
            <span className="gridHealthIcon" aria-hidden="true">
              <ConceptIcon
                concept={
                  inBlackout
                    ? "blackout"
                    : gridHealth.state === "stable"
                      ? "supply"
                      : "danger"
                }
                fontSize="small"
              />
            </span>
            <strong>{gridHealth.label}</strong>
          </span>
          <span className="gridHealthSeparator" aria-hidden="true">
            |
          </span>
          <strong className="gridHealthMetric">{gridHealth.metric}</strong>
        </div>
        {inBlackout && (
          <span className="gridHealthAdvice">
            Resume generation or discharge storage now.
          </span>
        )}
      </div>
      <span className="srOnly" aria-live="polite">
        {gridHealth.announcement}
      </span>
      <div
        id="yearProgressBar"
        style={{
          width: `${date.percentOfYear * 100}%`,
        }}
      />
      <ScenarioDetailsDialog
        open={scenarioDetailsOpen}
        game={game}
        onClose={() => setScenarioDetailsOpen(false)}
      />
    </div>
  );
}

const mapStateToProps = (state: AppStateType): StateProps => ({
  game: state.game,
});

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    onManual: () => {
      dispatch(navigate("MANUAL"));
    },
    onSettings: () => {
      dispatch(navigate("SETTINGS"));
    },
    onSpeedChange: (speed: SpeedType) => {
      dispatch(setSpeed(speed));
    },
    onNextTutorial: (scenarioId: number) => {
      startTutorial(dispatch, scenarioId);
    },
    onQuit: () => {
      dispatch(quit());
    },
  };
};

const GameAppBarContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(GameAppBar);

export default GameAppBarContainer;
